import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  ClickAwayListener,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Verified as VerifiedIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { SleeperAccount } from '../types';

interface NavSearchProps {
  onClose?: () => void;
}

export default function NavSearch({ onClose }: NavSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        setDebouncedQuery(query);
        setIsOpen(true);
      } else {
        setDebouncedQuery('');
        setIsOpen(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Search our database
  const { data: results, isLoading } = useQuery({
    queryKey: ['navSearch', debouncedQuery],
    queryFn: () => usersApi.searchUsers(debouncedQuery, { limit: 5 }),
    enabled: debouncedQuery.length >= 2,
  });

  // Lookup from Sleeper API
  const lookupMutation = useMutation({
    mutationFn: (username: string) => usersApi.lookupUser(username),
    onSuccess: (account) => {
      handleSelectUser(account);
    },
  });

  const handleSelectUser = (account: SleeperAccount) => {
    setQuery('');
    setIsOpen(false);
    onClose?.();
    navigate(`/sleeper/${account.sleeper_user_id}`);
  };

  const handleLookupFromSleeper = () => {
    if (query.length >= 2) {
      lookupMutation.mutate(query);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const showLookupOption = debouncedQuery.length >= 2 && 
    !isLoading && 
    (!results || results.length === 0 || 
     !results.some(r => r.current_username.toLowerCase() === debouncedQuery.toLowerCase()));

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: { xs: '100%', md: 280 } }}>
        <TextField
          ref={inputRef}
          size="small"
          placeholder="Search Sleeper users..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
              '&.Mui-focused': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              color: 'inherit',
              '& input': { color: 'inherit' },
              '& input::placeholder': { color: 'rgba(255, 255, 255, 0.7)', opacity: 1 },
            },
          }}
          fullWidth
        />

        {/* Dropdown Results */}
        {isOpen && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              zIndex: 1300,
            }}
          >
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!isLoading && results && results.length > 0 && (
              <List disablePadding>
                {results.map((account) => (
                  <ListItem
                    key={account.id}
                    onClick={() => handleSelectUser(account)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={
                          account.current_avatar_id
                            ? `https://sleepercdn.com/avatars/thumbs/${account.current_avatar_id}`
                            : undefined
                        }
                        sx={{ width: 36, height: 36 }}
                      >
                        {account.current_username?.[0]?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {account.current_username}
                          </Typography>
                          {account.is_verified && (
                            <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          )}
                        </Box>
                      }
                      secondary={account.current_display_name || undefined}
                    />
                    {account.is_verified && (
                      <Chip label="Verified" size="small" color="success" variant="outlined" />
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            {/* Option to lookup from Sleeper API */}
            {showLookupOption && (
              <>
                {results && results.length > 0 && <Divider />}
                <ListItem
                  onClick={handleLookupFromSleeper}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: 'primary.50',
                  }}
                  disabled={lookupMutation.isPending}
                >
                  <ListItemAvatar>
                    {lookupMutation.isPending ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PersonSearchIcon />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {lookupMutation.isPending 
                          ? `Looking up "${query}"...`
                          : `Find "${query}" on Sleeper`}
                      </Typography>
                    }
                    secondary={
                      lookupMutation.isPending 
                        ? 'Syncing user data...'
                        : 'Search Sleeper API and sync their leagues'
                    }
                  />
                </ListItem>
              </>
            )}

            {/* Error message */}
            {lookupMutation.isError && (
              <Box sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography variant="body2" color="error.contrastText">
                  User not found on Sleeper
                </Typography>
              </Box>
            )}

            {/* No results message */}
            {!isLoading && !showLookupOption && (!results || results.length === 0) && debouncedQuery && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No users found
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

